import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const socketRef = useRef(null);
  const notificationPermissionRef = useRef(null);
  const conversationsRef = useRef([]);
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);

  const apiBase = useMemo(() => 
    (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, ''), 
    []
  );

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        notificationPermissionRef.current = permission;
      });
    } else if ('Notification' in window) {
      notificationPermissionRef.current = Notification.permission;
    }
  }, []);

  // Load conversations and calculate unread count (with throttling)
  const loadConversations = async () => {
    if (!isAuthenticated()) {
      setUnreadCount(0);
      setConversations([]);
      conversationsRef.current = [];
      return [];
    }

    // Throttle: don't load if already loading or if last load was less than 2 seconds ago
    const now = Date.now();
    if (loadingRef.current || (now - lastLoadTimeRef.current < 2000)) {
      return conversationsRef.current;
    }

    loadingRef.current = true;
    lastLoadTimeRef.current = now;

    try {
      const r = await api.get('/chat/conversations');
      const convos = r.data.conversations || [];
      setConversations(convos);
      conversationsRef.current = convos;
      const totalUnread = convos.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadCount(totalUnread);
      return convos;
    } catch (e) {
      // Don't log 429 errors as they're expected during rate limiting
      if (e.response?.status !== 429) {
        console.error('Failed to load conversations:', e);
      }
      return conversationsRef.current;
    } finally {
      loadingRef.current = false;
    }
  };

  // Show browser notification
  const showNotification = (message, senderName) => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (document.hasFocus()) return; // Don't show if tab is active

    const notification = new Notification('New Message', {
      body: `${senderName}: ${message.body || (message.attachment_type === 'image' ? '📷 Image' : message.attachment_type === 'file' ? '📎 File' : '')}`,
      icon: '/logo.png',
      badge: '/logo.png',
      tag: `message-${message.conversation_id}`,
      requireInteraction: false,
      silent: false
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  };

  // Setup socket connection for real-time notifications
  useEffect(() => {
    if (!isAuthenticated() || !user) {
      setUnreadCount(0);
      setConversations([]);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setUnreadCount(0);
      setConversations([]);
      return;
    }

    const s = io(apiBase, {
      transports: ['websocket'],
      auth: { token }
    });
    socketRef.current = s;

    s.on('connect', () => {
      console.log('Notification socket connected');
      loadConversations();
    });

    s.on('connect_error', (err) => {
      console.error('Notification socket error:', err);
    });

    s.on('message:new', (msg) => {
      // Only show notification if message is not from current user
      if (msg.sender_id !== user.id) {
        // Get sender name from current conversations or fetch
        const convo = conversationsRef.current.find(c => c.id === msg.conversation_id);
        let senderName = 'Someone';
        
        if (convo) {
          senderName = `${convo.other_first_name} ${convo.other_last_name}`;
        } else {
          // Try to get from message data if available
          if (msg.sender_first_name && msg.sender_last_name) {
            senderName = `${msg.sender_first_name} ${msg.sender_last_name}`;
          }
        }
        
        // Show browser notification
        showNotification(msg, senderName);
        
        // Show toast notification
        toast.info(`New message from ${senderName}`, {
          position: 'top-right',
          autoClose: 3000,
          onClick: () => {
            // Could navigate to messages page
            window.location.href = '/messages';
          }
        });

        // Update unread count (throttled)
        setTimeout(() => {
          loadConversations().catch(() => {});
        }, 1000);
      }
    });

    s.on('conversation:updated', () => {
      // Throttle updates
      setTimeout(() => {
        loadConversations().catch(() => {});
      }, 1000);
    });

    // Load conversations on mount
    loadConversations();

    // Refresh conversations every 2 minutes (reduced frequency to avoid rate limiting)
    // Only poll when page is visible
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadConversations().catch(() => {});
      }
    }, 120000); // 2 minutes

    return () => {
      s.disconnect();
      clearInterval(interval);
    };
  }, [isAuthenticated, user?.id, apiBase]);

  // Refresh count when user changes
  useEffect(() => {
    if (isAuthenticated()) {
      loadConversations();
    } else {
      setUnreadCount(0);
      setConversations([]);
      conversationsRef.current = [];
    }
  }, [isAuthenticated, user?.id]);

  const markAsRead = async (conversationId) => {
    try {
      await api.post(`/chat/conversations/${conversationId}/read`);
      await loadConversations();
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  };

  const value = {
    unreadCount,
    conversations,
    loadConversations,
    markAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

